// A floor tile's quick actions (spec/13 §3.1): the common live-ops on one station without opening its modal —
// Message, Add free time, Lock, Maintenance and power — behind a "⋯" button that shows on hover or focus, and on a
// right-click anywhere on the tile. The gates, forms and confirms are the station modal's own
// (station-actions.tsx): lock and ending maintenance go at once with Undo, restart and shut down confirm, and a
// form opens in a small dialog over the floor. "Open station" opens the modal. A PC wearing the health badge offers
// "Clear health warnings…" to a role with `stations:alerts_clear` (F-STN-11, 2026-10-02): clear or snooze them all.

import type { FloorStationState, Session } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Menu, type MenuEntry } from '@clavian/ui-web';
import { useState, type MouseEvent, type ReactNode } from 'react';

import type { Access } from '../api/queries';
import { CompForm } from '../org/sessions/StationSessionPanel';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { ClearAlertsForm, STATIONS_ALERTS_CLEAR } from './clear-alerts';
import type { FloorStationView } from './live';
import { MaintenanceControls, MessageControls, PowerConfirm, runningSession, stationActionGates, useMaintenance, usePowerNow, useSessionLock } from './station-actions';

type QuickForm = 'message' | 'comp' | 'maintenance' | 'restart' | 'shutdown' | 'clearAlerts';

/** The tile with its quick actions: the tile itself is `children`, the menu sits in its corner. */
export function TileWithQuickActions({
  view,
  access,
  onOpen,
  children,
}: {
  readonly view: FloorStationView;
  readonly access: Access;
  readonly onOpen: (stationId: string) => void;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const station = view.station;
  const name = station.name ?? t('floor.tile.numbered', { number: station.number });
  const canSessions = access.status === 'ready' && access.can('sessions:read');
  // The floor reads the session of every occupied station (live.ts); a role that cannot read sessions gets none.
  const running = canSessions ? runningSession(view.session) : null;
  const gates = stationActionGates(access, running, station);
  const lock = useSessionLock();
  const power = usePowerNow(station);
  const maintenance = useMaintenance(station);
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState<QuickForm | null>(null);
  const locked = view.state === 'locked' || running?.status === 'locked';
  // F-STN-11 (2026-10-02): a PC wearing the health badge can have its warnings cleared (or snoozed) from here.
  const canClearAlerts = access.status === 'ready' && access.can(STATIONS_ALERTS_CLEAR) && (station.health?.openAlerts ?? 0) > 0;

  const items: MenuEntry[] = [
    ...(gates.message ? [{ id: 'message', label: t('floor.drawer.quick.message'), icon: <Icon name="message" />, onSelect: () => setForm('message') }] : []),
    ...(gates.comp ? [{ id: 'comp', label: t('floor.drawer.quick.addTime'), icon: <Icon name="gift" />, onSelect: () => setForm('comp') }] : []),
    ...(gates.lock && running !== null
      ? [
          {
            id: 'lock',
            label: locked ? t('sessions.actions.unlock') : t('sessions.actions.lock'),
            icon: <Icon name={locked ? 'unlock' : 'lock'} />,
            disabled: lock.pending,
            onSelect: () => lock.send(running.stationId, !locked, true, running),
          },
        ]
      : []),
    ...(gates.maintenance
      ? [
          maintenance.inMaintenance
            ? { id: 'maintenance', label: t('floor.drawer.endMaintenance'), icon: <Icon name="wrench" />, disabled: maintenance.pending, onSelect: () => maintenance.end(true) }
            : { id: 'maintenance', label: t('floor.drawer.quick.maintenance'), icon: <Icon name="wrench" />, onSelect: () => setForm('maintenance') },
        ]
      : []),
    ...(canClearAlerts ? [{ id: 'clearAlerts', label: t('floor.drawer.quick.clearAlerts'), icon: <Icon name="check" />, onSelect: () => setForm('clearAlerts') }] : []),
    ...(gates.power
      ? ([
          { kind: 'separator', id: 'power-separator' },
          { id: 'restart', label: t('floor.drawer.quick.restart'), icon: <Icon name="refresh" />, onSelect: () => setForm('restart') },
          { id: 'shutdown', label: t('floor.drawer.quick.shutdown'), icon: <Icon name="power" />, tone: 'danger', onSelect: () => setForm('shutdown') },
          { id: 'wake', label: t('floor.power.wake'), icon: <Icon name="bolt" />, disabled: power.pending, onSelect: () => power.send('wake') },
        ] satisfies MenuEntry[])
      : []),
  ];

  // No quick action this role may take (or none yet — a session's actions arrive once it is read): the tile opens the
  // modal as it always did, and nothing more. The wrapper is drawn either way, so the tile is never remounted — and
  // never loses focus — when the menu arrives.
  const all: MenuEntry[] =
    items.length === 0 ? [] : [...items, { kind: 'separator', id: 'open-separator' }, { id: 'open', label: t('floor.drawer.quick.open'), onSelect: () => onOpen(station.stationId) }];

  const onContextMenu = (event: MouseEvent) => {
    if (all.length === 0) return;
    event.preventDefault();
    setMenuOpen(true);
  };

  return (
    <div className="console-tile-wrap" onContextMenu={onContextMenu}>
      {children}
      {all.length === 0 ? null : (
        <Menu
          align="end"
          items={all}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          trigger={<Button variant="ghost" size="sm" className="console-tile-wrap__more" icon={<Icon name="more" />} label={t('floor.drawer.quick.label', { name })} />}
        />
      )}
      {form === null ? null : (
        <QuickFormDialog
          form={form}
          station={station}
          name={name}
          organizationId={venue.organizationId}
          session={running}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

function QuickFormDialog({
  form,
  station,
  name,
  organizationId,
  session,
  onClose,
}: {
  readonly form: QuickForm;
  readonly station: FloorStationState;
  readonly name: string;
  readonly organizationId: string;
  /** The session running on the station: free time goes to it, and a message is recorded on it. */
  readonly session: Session | null;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const title =
    form === 'message'
      ? t('floor.drawer.message.title')
      : form === 'comp'
        ? t('floor.drawer.bar.addTime')
        : form === 'maintenance'
          ? t('floor.drawer.maintenance')
          : form === 'clearAlerts'
            ? t('floor.drawer.health.clearForm.titleAll')
            : t(`floor.power.${form}`);
  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={title}
      description={name}
      className="console-quick-dialog"
      // The menu item that opened it is gone: focus returns to the station's tile.
      onCloseAutoFocus={(event) => {
        const tile = document.querySelector<HTMLElement>(`[data-station-tile="${station.stationId}"]`);
        if (tile === null) return;
        event.preventDefault();
        tile.focus();
      }}
    >
      {form === 'message' ? <MessageControls station={station} session={session} plain /> : null}
      {form === 'comp' && session !== null ? <CompForm organizationId={organizationId} sessionId={session.id} onDone={onClose} /> : null}
      {form === 'maintenance' ? <MaintenanceControls station={station} plain onStarted={onClose} /> : null}
      {form === 'restart' || form === 'shutdown' ? <PowerConfirm station={station} action={form} plain onDone={onClose} /> : null}
      {form === 'clearAlerts' ? <ClearAlertsForm station={station} onDone={onClose} /> : null}
    </Dialog>
  );
}
